import { useCallback, useEffect, useState } from 'react';
import { selectAdvancedPriceComponentFilters } from '../filters/advancedFiltersSelectors';
import { useDebouncedState } from '../helpers/hooks';
import {
  useGetPriceComponentsQuery,
  useLazyGetPriceComponentsQuery,
} from '../services/edgeApi';
import { PriceComponent } from '@/types';
import { useSelector } from 'react-redux';

export const usePriceComponents = ({ pageSize = 30 } = {}) => {
  const { tsps, textSearch, hasPoints, mapped, priceTypes } = useSelector(
    selectAdvancedPriceComponentFilters,
  );

  const [shouldFetch, setShouldFetch] = useState<boolean>(true);
  const [exhausted, setExhausted] = useState<boolean>(false);
  const [cursor, setCursor] = useState<number>(0);
  const [priceComponents, setPriceComponents] = useState<
    Record<string, PriceComponent>
  >({});

  const [debouncedSearch, setDebouncedSearch] = useDebouncedState(
    textSearch,
    500,
  );

  useEffect(() => {
    setDebouncedSearch(textSearch);
  }, [textSearch]);

  useEffect(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setPriceComponents({});
  }, [debouncedSearch, tsps, hasPoints, mapped, priceTypes]);

  const { data, isLoading, isFetching, isError } = useGetPriceComponentsQuery(
    {
      limit: pageSize,
      offset: cursor,
      hasPoints,
      mapped,
      tsps,
      priceTypes,
      textSearch: debouncedSearch,
    },
    {
      skip: !shouldFetch || exhausted,
    },
  );

  const [getPriceComponentsTrigger] = useLazyGetPriceComponentsQuery();

  if (shouldFetch && isFetching === false && exhausted === false) {
    setPriceComponents((prevComponents) => ({ ...prevComponents, ...data }));
    setShouldFetch(false);
    if (data && Object.values(data).length > 0) {
      setCursor((prevCursor) => prevCursor + Object.values(data).length);
    }
    if (data && Object.values(data).length < pageSize) {
      setExhausted(true);
    }
  }

  const next = useCallback(() => {
    setShouldFetch(true);
  }, [setShouldFetch]);

  const reset = useCallback(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setPriceComponents({});
  }, [setShouldFetch, setCursor, setExhausted, setPriceComponents]);

  const refresh = useCallback(() => {
    // Add a slight delay to the refresh
    setTimeout(async () => {
      try {
        const result = await getPriceComponentsTrigger({
          limit: cursor,
          offset: 0,
          hasPoints,
          mapped,
          tsps,
          textSearch: debouncedSearch,
        }).unwrap();
        if (result) {
          setPriceComponents({ ...result });
        }
      } catch (error) {
        console.error('Error refreshing price components:', error);
      }
    }, 300);
  }, [
    getPriceComponentsTrigger,
    cursor,
    hasPoints,
    mapped,
    tsps,
    debouncedSearch,
  ]);

  return {
    priceComponents,
    isLoading,
    isFetching,
    isError,
    exhausted,
    next,
    reset,
    refresh,
  };
};
